import ToggleSwitch from 'components/ToggleSwitch'
import useToggleString from 'hooks/useToggleString'
import { Element } from 'react-scroll'
import { Polygon, Marker } from '@react-google-maps/api'
import { Position } from 'geojson'
import iconNotSelected from 'assets/default/svg/iconNotSelected.svg'
import iconSelected from 'assets/default/svg/iconSelected.svg'
import SideAndMainLayout from 'layouts/SideAndMainLayout/SideAndMainLayout'
import useFetchFavoris from 'hooks/useFetchFavoris'
import CardFavoris from 'components/Card/Card/CardFavori'
import Map from 'components/Map/Map'
import TripleGrow from 'components/Loading/TripleGrow'
import pluralizer from 'utils/pluralizer'
import DeleteFavoris from 'components/DeleteFavoris'
import { Status } from 'redux/features/favoris/slice'
import { Button, ButtonGroup } from 'react-bootstrap'
import cn from 'classnames'
import { useAppSelector } from 'redux/hooks'
import useDocumentTitle from 'hooks/useDocumentTitle'
import ExportFavoris from 'components/ExportFavoris'
import ToastAlert from 'components/ToastAlert'
import { useEffect, useState } from 'react'
import useGoogleMap from 'hooks/useGoogleMap'
import { makeSelectZoom } from 'redux/features/map/selectors'
import s from './Favoris.module.scss'

const Favoris = (): JSX.Element => {
  const [viewMode, toggle] = useToggleString(['map', 'list'])
  const {
    favoris,
    status,
    error,
    exportError,
    uncheckAll,
    checkAll,
    isSelected,
    selectFavori,
    countFavorisSaved,
    countFavorisChecked,
  } = useFetchFavoris()
  const zoom = useAppSelector(makeSelectZoom)
  const map = useGoogleMap()
  const [checkmode, setCheckmode] = useState<'' | 'export' | 'delete'>('')
  useDocumentTitle('Favoris')

  /*
   * It seems useless but it forces the card list to re-render between viewMode changes
   * else the truncated commentaire will render incorrectly
   */
  const [, setRender] = useState(false)
  useEffect(() => {
    if (status === Status.Succeeded && favoris.length > 0)
      setRender((prev) => !prev)
  }, [viewMode, status, favoris.length])

  const isSucceeded = status === Status.Succeeded && favoris.length > 0

  return (
    <SideAndMainLayout
      asideClassName={cn({
        [s.listView]: viewMode === 'list',
      })}
      aside={
        <>
          <ToastAlert show={status === Status.Failed} variant="danger">
            {error}
          </ToastAlert>
          <div
            id="listing-favoris"
            className={cn(s.favorisListing, s[viewMode])}
          >
            <ToggleSwitch
              className={s.toggleSwitch}
              value={viewMode === 'list'}
              onChange={() => toggle()}
              labelLeft="Vue Carte"
              labelRight="Vue liste"
            />
            <h2>
              {pluralizer(
                [
                  'Aucun bien dans vos favoris :',
                  '{{count}} bien dans vos favoris :',
                  '{{count}} biens dans vos favoris :',
                ],
                countFavorisSaved
              )}
            </h2>
            <p>
              Exportez une sélection de vos favoris pour partir en prospection.
              Enfilez vos baskets et c’est parti ! Bonne prospection !
            </p>

            <div
              className={cn(s.selectionControls, {
                [s.mapView]: viewMode === 'map',
                [s.show]: checkmode !== '' && countFavorisSaved > 0,
              })}
            >
              <ButtonGroup
                className={s.btnGroupSelection}
                aria-label="Control selection"
              >
                <Button
                  variant="link"
                  onClick={checkAll}
                  disabled={!isSucceeded}
                >
                  Sélectionner tous les biens
                </Button>
                <Button
                  variant="link"
                  onClick={uncheckAll}
                  disabled={!isSucceeded}
                >
                  Effacer la sélection
                </Button>
              </ButtonGroup>
            </div>
            <div className={s.listing}>
              {status === Status.Pending && (
                <div className={s.loading}>
                  <TripleGrow />
                </div>
              )}
              {status === Status.Succeeded &&
                favoris.length > 0 &&
                favoris.map((favori, idx) => (
                  <Element
                    name={favori.uidParcelle}
                    key={favori.uidParcelle}
                    className={s.cardFavoriWrapper}
                  >
                    <CardFavoris
                      count={{ index: idx + 1, total: favoris.length }}
                      variant={viewMode as 'list' | 'map'}
                      favori={favori}
                      onClick={() => selectFavori(favori.uidParcelle)}
                      showCheckbox={checkmode !== ''}
                    />
                  </Element>
                ))}
            </div>
          </div>
          <div
            className={cn(s.validation, {
              [s.show]: status === Status.Succeeded && favoris.length > 0,
            })}
          >
            {exportError && <p className="text-danger">{exportError}</p>}
            {checkmode === 'export' && (
              <>
                <p
                  className={s.favorisSelected}
                  // eslint-disable-next-line react/no-danger -- Not a danger because value not from public API. Needed to apply the bold style
                  dangerouslySetInnerHTML={{
                    __html: pluralizer(
                      [
                        "<b>Aucun bien</b> sélectionné pour l'exportation",
                        "<b>{{count}} bien</b> sélectionné pour l'exportation",
                        "<b>{{count}} biens</b> sélectionnés pour l'exportation",
                      ],
                      countFavorisChecked
                    ),
                  }}
                />
                <ExportFavoris disabled={!countFavorisChecked}>
                  Valider la selection
                </ExportFavoris>
              </>
            )}
            {checkmode === 'delete' && (
              <>
                <p
                  className={s.favorisSelected}
                  // eslint-disable-next-line react/no-danger -- Not a danger because value not from public API. Needed to apply the bold style
                  dangerouslySetInnerHTML={{
                    __html: pluralizer(
                      [
                        '<b>Aucun bien</b> sélectionné pour la suppression',
                        '<b>{{count}} bien</b> sélectionné pour la suppression',
                        '<b>{{count}} biens</b> sélectionnés pour la suppression',
                      ],
                      countFavorisChecked
                    ),
                  }}
                />
                <DeleteFavoris
                  className={s.btnDeleteAll}
                  disabled={!countFavorisChecked}
                >
                  Valider la selection
                </DeleteFavoris>
              </>
            )}
            {checkmode === '' && (
              <>
                <Button
                  id="page-favoris-btn-exporter"
                  onClick={() => setCheckmode('export')}
                >
                  Exporter des favoris
                </Button>
                <Button onClick={() => setCheckmode('delete')}>
                  Supprimer des favoris
                </Button>
              </>
            )}
          </div>
        </>
      }
      main={
        (viewMode === 'map' && status === Status.Succeeded && (
          <Map>
            <>
              {favoris.map((favori) => {
                const centerMarker = {
                  lat: favori.parcelleCentre.latitude,
                  lng: favori.parcelleCentre.longitude,
                }
                const coordGlobal = favori.parcelleGeography.coordinates.map(
                  (coordinateArray: Position[]) =>
                    coordinateArray.map((coordinate) => ({
                      lat: coordinate[1],
                      lng: coordinate[0],
                    }))
                )
                const iconPin = {
                  url: iconNotSelected,
                }

                const pathOptions = {
                  strokeColor: s.colorParcelle,
                  strokeOpacity: 1,
                  strokeWeight: 3,
                  zIndex: 1,
                  fillOpacity: 0.15,
                }
                if (isSelected(favori.uidParcelle)) {
                  iconPin.url = iconSelected
                  pathOptions.strokeColor = s.colorParcelleSelected
                  pathOptions.zIndex = 2
                  pathOptions.fillOpacity = 0
                }
                // 17 correspond au passage en mode "bâtiments en 3D"
                return map && zoom && zoom > 17 ? (
                  <Polygon
                    key={favori.uidParcelle}
                    paths={coordGlobal}
                    options={pathOptions}
                    onClick={() => selectFavori(favori.uidParcelle)}
                  />
                ) : (
                  <Marker
                    key={favori.uidParcelle}
                    position={centerMarker}
                    icon={iconPin}
                    options={pathOptions}
                    onClick={() => selectFavori(favori.uidParcelle)}
                  />
                )
              })}
            </>
          </Map>
        )) ||
        null
      }
    />
  )
}

export default Favoris
